import { useState } from "react";
import { Link } from "react-router";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ArrowRight, CalendarClock } from "lucide-react";
import type { ProsthesisStatus } from "@bookdental/schemas";
import { Card, Skeleton } from "@bookdental/ui";
import { STATUS_PHASES } from "@/components/prosthesis/prosthesis-meta";
import { isUrgentDeadline } from "@/lib/prosthesis-urgency";
import { effectiveDeadline, serviceDeadlineBucket, type DeadlineBucket } from "@/lib/dashboard-lab";
import {
  flattenServiceRows,
  serviceLabel,
  situacaoDoServico,
  type IncomingOrderRow,
  type ServiceRow,
} from "@/components/production/production-types";
import { SeloVenceEmBreve } from "@/components/production/PrazoComAtraso";
import { CatalogoBusca, combina } from "@/components/catalogo/CatalogoBusca";
import { ProductionOrderDetailDialog } from "@/components/production/ProductionOrderDetailDialog";
import { ProductionServiceDetailDialog } from "@/components/production/ProductionServiceDetailDialog";
import type { PrazoDoLaboratorio } from "@bookdental/schemas";

const BUCKETS: { key: DeadlineBucket; label: string; accent: string }[] = [
  { key: "atrasado", label: "Atrasado", accent: "#ef4444" },
  { key: "hoje", label: "Hoje", accent: "#2457ff" },
  { key: "proximos7", label: "Próximos 7 dias", accent: "#0ea5e9" },
  { key: "proximos30", label: "Próximos 30 dias", accent: "#f59e0b" },
];

const MAX_PER_BUCKET = 6;

/** Etapa do fluxo (Recebido/Planejamento/Produção/Retrabalho/Envio/Finalizado) a que um status
 *  pertence — mesmo agrupamento usado no Kanban e nos KPIs, só que aqui pro badge do card. */
function phaseOf(status: ProsthesisStatus) {
  return STATUS_PHASES.find((p) => p.statuses.includes(status))!;
}

type Props = {
  orders: IncomingOrderRow[];
  loading: boolean;
  prazo: PrazoDoLaboratorio;
  /** Quando true, desabilita o clique nos cards (não abre diálogo de detalhe) e esconde os links
   *  pra /lab/producao — usado no preview cross-org de outra organização (ver
   *  organization/painel-geral/AgirNaOrganizacao.tsx), onde os diálogos de detalhe e a rota de produção presumiriam a
   *  organização ativa errada. */
  readOnly?: boolean;
  /** Esconde só os links pra /lab/producao e mantém os diálogos — o painel de /organizacoes age
   *  na organização certa (cabeçalho `x-organizacao`), mas navegar abriria a ativa. */
  semLinks?: boolean;
};

/** Resumo do dia do laboratório organizado por prazo (o que vence primeiro aparece primeiro),
 *  1 card por serviço (não por pedido — um pedido com 2 serviços em fases diferentes aparece nos
 *  2 buckets certos) e ordenado por status dentro de cada bucket. */
export function LabDeadlineSummary({ orders, loading, prazo, readOnly = false, semLinks = false }: Props) {
  const comLinks = !readOnly && !semLinks;
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [detailServiceId, setDetailServiceId] = useState<string | null>(null);

  // Busca por paciente: filtra na tela e, buscando, mostra todos os achados (sem o limite por coluna).
  const [busca, setBusca] = useState("");
  const buscando = !!busca.trim();
  const allRows = flattenServiceRows(orders);
  const rowsVisiveis = buscando ? allRows.filter((r) => combina(r.order.patientName, busca.trim())) : allRows;
  const detailOrder = orders.find((o) => o.id === detailOrderId) ?? null;
  const detailRow = allRows.find((r) => r.service?.id === detailServiceId) ?? null;

  const openOrder = (order: IncomingOrderRow) => {
    setDetailServiceId(null);
    setDetailOrderId(order.id);
  };
  const openService = (row: ServiceRow) => {
    if (readOnly) return;
    setDetailServiceId(row.service?.id ?? null);
  };

  const grouped = BUCKETS.map((bucket) => {
    const bucketRows = rowsVisiveis
      .filter((r) => serviceDeadlineBucket(r, prazo) === bucket.key)
      .sort(
        (a, b) =>
          STATUS_PHASES.indexOf(phaseOf(a.service!.status)) -
          STATUS_PHASES.indexOf(phaseOf(b.service!.status)),
      );
    const visibleRows = buscando ? bucketRows : bucketRows.slice(0, MAX_PER_BUCKET);
    const phaseGroups = STATUS_PHASES.map((phase) => ({
      phase,
      rows: visibleRows.filter((r) => phaseOf(r.service!.status) === phase),
    })).filter((g) => g.rows.length > 0);
    return { ...bucket, rows: bucketRows, phaseGroups, hiddenCount: bucketRows.length - visibleRows.length };
  }).filter((bucket) => bucket.rows.length > 0);

  return (
    <Card className="p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700 dark:bg-primary/15 dark:text-primary">
            <CalendarClock className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-[15px] font-semibold text-zinc-900 dark:text-foreground">
              Resumo por prazo
            </h2>
            <p className="text-xs text-zinc-500 dark:text-muted-foreground">
              Serviços em aberto, pelo que vence primeiro
            </p>
          </div>
        </div>
        {comLinks && (
          <Link
            to="/lab/producao"
            className="-mx-2 inline-flex h-11 shrink-0 items-center gap-1 rounded-md px-2 text-sm font-medium text-brand-700 transition-colors hover:text-brand-800 md:mx-0 md:h-auto md:px-0 dark:text-primary"
          >
            Ver produção completa
            <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </div>

      <CatalogoBusca valor={busca} onChange={setBusca} placeholder="Buscar paciente" achados={grouped.reduce((n, b) => n + b.rows.length, 0)} className="mt-4 sm:max-w-xs" />

      <div className="mt-4">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : grouped.length === 0 ? (
          <p className="py-6 text-center text-sm text-zinc-500 dark:text-muted-foreground">
            {buscando ? "Nenhum serviço em aberto desse paciente." : "Nenhum serviço em produção no momento."}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {grouped.map((bucket) => (
              <div
                key={bucket.key}
                className="rounded-xl border border-zinc-200 p-3.5 dark:border-border"
              >
                <p
                  className="mb-2 flex items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-wide"
                  style={{ color: bucket.accent }}
                >
                  {bucket.label}
                  <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-bold text-zinc-600 dark:bg-muted dark:text-muted-foreground">
                    {bucket.rows.length}
                  </span>
                </p>
                <div className="space-y-3">
                  {bucket.phaseGroups.map(({ phase, rows }) => (
                    <div
                      key={phase.key}
                      className="rounded-lg p-2"
                      style={{ backgroundColor: `${phase.accent}0d` }}
                    >
                      <p
                        className="mb-1 text-[10px] font-semibold uppercase tracking-wide"
                        style={{ color: phase.accent }}
                      >
                        {phase.label}
                      </p>
                      <div className="space-y-1.5">
                        {rows.map((row) => (
                          <ServiceCard
                            key={row.service!.id}
                            row={row}
                            prazo={prazo}
                            bucketKey={bucket.key}
                            onSelect={openService}
                            readOnly={readOnly}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                  {bucket.hiddenCount > 0 && comLinks && (
                    <Link
                      to="/lab/producao"
                      className="block px-1 py-1 text-xs font-medium text-brand-700 hover:underline dark:text-primary"
                    >
                      +{bucket.hiddenCount} serviço(s)
                    </Link>
                  )}
                  {bucket.hiddenCount > 0 && !comLinks && (
                    <p className="px-1 py-1 text-xs font-medium text-zinc-400 dark:text-muted-foreground">
                      +{bucket.hiddenCount} serviço(s)
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {!readOnly && (
        <>
          <ProductionServiceDetailDialog row={detailRow} onClose={() => setDetailServiceId(null)} onOpenOrder={openOrder} />
          <ProductionOrderDetailDialog order={detailOrder} onClose={() => setDetailOrderId(null)} />
        </>
      )}
    </Card>
  );
}

function ServiceCard({
  row,
  prazo,
  bucketKey,
  onSelect,
  readOnly,
}: {
  row: ServiceRow;
  prazo: PrazoDoLaboratorio;
  bucketKey: DeadlineBucket;
  onSelect: (row: ServiceRow) => void;
  readOnly?: boolean;
}) {
  const service = row.service!;
  const eff = effectiveDeadline(row.order, prazo);
  const venceEmBreve = situacaoDoServico(row, prazo) === "venceEmBreve";
  const urgente = !venceEmBreve && bucketKey !== "atrasado" && isUrgentDeadline(row.order.deadline, row.order.createdAt, prazo);

  return (
    <button
      type="button"
      onClick={() => onSelect(row)}
      disabled={readOnly}
      className="block w-full rounded-lg border border-zinc-200 bg-white p-2.5 text-left transition-all hover:border-zinc-300 hover:shadow-sm disabled:cursor-default disabled:hover:border-zinc-200 disabled:hover:shadow-none dark:border-border dark:bg-card"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="min-w-0 truncate text-sm font-medium text-zinc-800 dark:text-foreground">
          {row.order.patientName}
        </p>
        {venceEmBreve && <SeloVenceEmBreve compacto />}
        {urgente && (
          <span className="shrink-0 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-600 dark:bg-amber-500/10 dark:text-amber-400">
            Urgente
          </span>
        )}
      </div>
      <div className="mt-0.5 flex items-center justify-between gap-2">
        <span className="min-w-0 truncate text-xs text-zinc-500 dark:text-muted-foreground">
          {row.order.organization.name} · {serviceLabel(service)}
        </span>
        <span className="shrink-0 text-xs text-zinc-400 dark:text-muted-foreground">
          {format(eff, "dd/MM", { locale: ptBR })}
        </span>
      </div>
    </button>
  );
}
